import { useCallback, useEffect, useState } from "react";
import { useRefreshDevice } from "@/features/cards/device/hooks/use-refresh-device";

import { OPERATING_MODE } from "@/services/device-attributes/attributes/operating-mode/definitions";
import { composeDeviceAttributes, DeviceAttributes } from "@/services/device-attributes/device-attributes";
import { Icon } from "../../../../features/map-markers/device-marker/components/icon/icon";
import {
    makeIconConfig,
    markerIconSizeScaling,
} from "../../../../features/map-markers/device-marker/components/icon/icon-config";
import { DeviceMarker } from "../../../../features/map-markers/device-marker/device-marker";
import { isDeviceInManualMode } from "../../../device-attributes/attributes/operating-mode/getters";
import MapEntityBase from "../../map-entities/map-entity-base";
import {
    disabledByNotes,
    makeBadgeParams,
    makeColorParams,
    makeWarnings,
    shouldExpand,
} from "../../map-entities/shared";
import { useIconSize } from "../core/sizing";
import { useProgressRingParams } from "../hooks/use-progress-ring-params";

const MAX_ZOOM_FOR_EXPANDED_ICONS = 19;

const makeIconState = (deviceModel: MapEntityBase["model"]) => ({
    warnings: makeWarnings({ model: deviceModel, type: deviceModel.type }),
    colorParams: makeColorParams({ deviceType: deviceModel.type, notes: deviceModel.notes }),
    manualMode: isDeviceInManualMode({ model: deviceModel }),
});

/**
 * The progress ring should tick if the device has an open valve and is not in manual mode
 */
export const isProgressRingActive = ({
    operatingMode,
    irrigation,
}: {
    operatingMode: OPERATING_MODE | null;
    irrigation: DeviceAttributes["irrigation"] | undefined;
}) => {
    if (!irrigation) {
        return false;
    }
    return Boolean(irrigation.hasOpenValve && operatingMode !== OPERATING_MODE.MANUAL_OPEN);
};

interface DeviceMapEntityIconProps {
    deviceModel: MapEntityBase["model"];
    onClickIcon: (e: React.MouseEvent<HTMLDivElement>) => void;
    onHover: (e: React.MouseEvent<HTMLDivElement>, isHovered: boolean) => void;
    mapComponent: L.Map;
    currentMapZoom: number;
    dimmed?: boolean;
    highlighted?: boolean;
    hidden?: boolean;
}

/**
 * Renders an individual device icon. Wraps the DeviceIcon component, and contains logic for what an icon should look like
 * based on the device document and which props to pass in to the DeviceIcon component
 */
export function DeviceMapEntityIcon({
    deviceModel,
    onClickIcon,
    onHover,
    mapComponent,
    currentMapZoom,
    dimmed,
    highlighted,
    hidden,
}: DeviceMapEntityIconProps) {
    const iconSize = useIconSize({ mapComponent });
    const [deviceState, setDeviceState] = useState(() => makeIconState(deviceModel));

    // Determine if the progress ring should tick based on the device model
    const shouldProgressRingTick = useCallback(() => {
        const { operatingMode: newOperatingMode, irrigation: newIrrigation } = composeDeviceAttributes({
            model: deviceModel,
        });
        return isProgressRingActive({ operatingMode: newOperatingMode.operatingMode, irrigation: newIrrigation });
    }, [deviceModel]);

    // Use the hook to manage the progress ring params
    const { progressRingParams, evaluateProgressRingParams } = useProgressRingParams({
        model: deviceModel,
        shouldTickFn: shouldProgressRingTick,
    });

    // Update whenever model is refreshed via events or polling
    useRefreshDevice({
        model: deviceModel,
        onRefresh: () => {
            setDeviceState(makeIconState(deviceModel));
            evaluateProgressRingParams(); // Force re-evauation of progress ring params based on new model
        },
    });

    useEffect(() => {
        setDeviceState(makeIconState(deviceModel));
    }, [deviceModel]);

    if (hidden) {
        return <></>;
    }

    // Keep expanded if inManualMode, has a warning, or is irrigating
    const shouldExpandBasedOnState = shouldExpand({
        model: deviceModel,
        warningToShow: deviceState.warnings.warningToShow,
        warningStatus: deviceState.warnings.status,
    });

    const { variant, iconConfig } = makeIconConfig({
        manualMode: deviceState.manualMode,
        deviceType: deviceModel.type,
        connectedEquipment: deviceModel.connectedEquipment,
    });

    return (
        <DeviceMarker
            tooltip={{
                deviceModel,
                enabled: true,
            }}
            dimmed={dimmed}
            size={iconSize}
            forceExpand={
                highlighted ||
                (!disabledByNotes(deviceModel.notes) &&
                    (currentMapZoom >= MAX_ZOOM_FOR_EXPANDED_ICONS || shouldExpandBasedOnState))
            }
            onClick={onClickIcon}
            onMouseEnter={(e) => onHover(e, true)}
            onMouseLeave={(e) => onHover(e, false)}
            colorParams={deviceState.colorParams}
            badgeParams={makeBadgeParams({ warningStatus: deviceState.warnings.status })}
            progressRingParams={progressRingParams}
        >
            <Icon
                size={iconSize * markerIconSizeScaling[variant]}
                color={deviceState.colorParams.iconColor}
                {...iconConfig}
            />
        </DeviceMarker>
    );
}
